import { CheckIcon, PencilIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { patchClientAccount } from '../../../../api/SageAdmin/ClientAccount';
import type { IPatchClientAccountPayload } from '../../../../api/SageAdmin/ClientAccount/types';
import { ReactQueryKey } from '../../../../constants/ReactQueryKeys';
import type { ClientDetailsInterface } from '../../../../pages/SageAdmin/Clients/Details/types';
import { authSelector } from '../../../../store';
import { notifyUser } from '../../../../store/Toast';
import type { ReactSelectOption } from '../../../../types/Constants';
import { sanitizeResponseError } from '../../../../utils';
import { HasPermission } from '../../../Permission/HasPermission';
import { HookForm } from '../../../UI/HookForm/Form';
import { HookInput } from '../../../UI/HookForm/Form/Input';
import HookMaskedInput, { type MaskedComponentType } from '../../../UI/HookForm/Form/MaskedInput';
import { HookReactSingleSelect } from '../../../UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '../../../UI/HookForm/Provider';

interface IEditableViewProps {
  company: Partial<ClientDetailsInterface>;
  name: keyof ClientDetailsInterface;
  successMessage: string;
  failureMessage?: string;
  view: React.ReactNode;
  disabled?: boolean;
  options?: ReactSelectOption[];
  type?: 'input' | 'select' | 'masked';
  validationSchema?: any;
  maskType?: MaskedComponentType;
  maxLength?: number;
  minLength?: number;
  defaultValue?: { [key: string]: any };
}

const EditableViewForm = (props: IEditableViewProps) => {
  const { data: currentUserData } = useSelector(authSelector);
  const { accountId } = currentUserData;
  const { company, name, successMessage, view, options, disabled, type = 'input', maxLength, minLength, maskType, defaultValue } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { reset } = useFormContext();

  const toggleEdit = () => {
    let viewDefaultValues = { [name]: company[name] };
    if (defaultValue) {
      viewDefaultValues = defaultValue;
    }
    reset(viewDefaultValues);
    setIsEdit((prev) => !prev);
  };
  const updateCompanyProfile = useMutation((data: Partial<IPatchClientAccountPayload>) => patchClientAccount({ data, accountId }), {
    onSuccess: () => {
      queryClient.invalidateQueries([ReactQueryKey.GET_CLIENT_ACCOUNT]);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: successMessage,
          toastType: 'success',
        })
      );
      toggleEdit();
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const onSubmit = (formData: any) => {
    let data = { ...formData };
    if (type === 'select') {
      data = { [name]: formData[name].value };
    }

    if (name === 'clientRep') {
      data = { clientRepId: formData[name].value };
    }

    updateCompanyProfile.mutate(data);
  };

  return (
    <HookForm onSubmit={onSubmit} className='flex justify-between '>
      {isEdit ? (
        <div className='w-1/2'>
          {type === 'select' && <HookReactSingleSelect id={name} options={options} name={name} />}
          {type === 'input' && (
            <HookInput type='text' name={name} disabled={updateCompanyProfile.isLoading} maxLength={maxLength} minLength={minLength} />
          )}
          {type === 'masked' && maskType && <HookMaskedInput id={name} name={name} maskedComponent={maskType} />}
        </div>
      ) : (
        <span className='flex-grow'>{view}</span>
      )}
      {!disabled && (
        <HasPermission of='UPDATE_COMPANY_PROFILE'>
          <span className='ml-4 shrink-0'>
            {!isEdit ? (
              <button
                type='button'
                key='update'
                className='bg-white rounded-md font-medium text-neutralColor-light hover:text-primaryColor-normal'
                onClick={toggleEdit}
              >
                <PencilIcon className='h-5 w-5' />
              </button>
            ) : (
              <button
                type='submit'
                key='submit'
                disabled={updateCompanyProfile.isLoading}
                className='bg-white rounded-md font-medium text-primaryColor-normal'
              >
                {updateCompanyProfile.isLoading ? (
                  'Saving...'
                ) : (
                  <div className='flex gap-2'>
                    <div className='bg-neutralColor-lightest hover:bg-neutralColor-lighter rounded-full p-1'>
                      <CheckIcon className='h-5 w-5' />
                    </div>
                    <div
                      className='p-1 rounded-full bg-neutralColor-lightest hover:bg-neutralColor-lighter cursor-pointer'
                      onClick={toggleEdit}
                    >
                      <XCircleIcon className='h-5 w-5 text-tertiaryColor-light hover:text-tertiaryColor-normal' />
                    </div>
                  </div>
                )}
              </button>
            )}
          </span>
        </HasPermission>
      )}
    </HookForm>
  );
};

export const EditableView = (props: IEditableViewProps) => {
  const { company, name, validationSchema, defaultValue } = props;

  let viewDefaultValues = { [name]: company[name] };
  if (defaultValue) {
    viewDefaultValues = defaultValue;
  }

  return (
    <HookFormProvider defaultValues={viewDefaultValues} resolver={yupResolver(validationSchema)}>
      <EditableViewForm {...props} />
    </HookFormProvider>
  );
};
